import Phaser from "phaser";
import { shouldRestartOnQuit } from "../flow/entry";
import { SceneKey, assertTransition } from "../flow/game-flow";
import {
  defaultArtLabState,
  markCompositeSeen,
  type ArtLabState,
} from "../features/art-lab/art-lab-state";
import { ArtLabStage } from "../features/art-lab/art-lab-stage";
import { sharedInput } from "../input/input-controller";
import { mountArtLabHud } from "../ui/huds/art-lab";
import { getUiLayout } from "../ui/layout";
import { clearOverlay } from "../ui/screen-overlay";
import { markActiveScene } from "../ui/screen-state";

/**
 * Art Lab — Body / Rig / VFX / Composite / Contract without full combat.
 */
export class ArtLabScene extends Phaser.Scene {
  private state!: ArtLabState;
  private hud!: ReturnType<typeof mountArtLabHud>;
  private stage!: ArtLabStage;
  private disposed = false;

  constructor() {
    super(SceneKey.ArtLab);
  }

  create(): void {
    this.disposed = false;
    markActiveScene(SceneKey.ArtLab);
    sharedInput.attach();
    clearOverlay();
    getUiLayout().sync();

    this.cameras.main.setBackgroundColor("#111b28");
    this.state = defaultArtLabState();
    this.stage = new ArtLabStage(this);
    this.stage.create();

    this.hud = mountArtLabHud({
      onPanel: (p) => {
        this.state.panel = p;
        this.hud.sync(this.state);
      },
      onChange: (patch) => {
        Object.assign(this.state, patch);
        this.hud.sync(this.state);
      },
      onPlayVfx: () => {
        this.stage.playVfxBurst(this.state);
        Object.assign(
          this.state,
          markCompositeSeen(this.state, this.state.affiliation),
        );
        this.hud.sync(this.state);
      },
      onSwing: () => this.stage.triggerAttack(),
      onQuit: () => this.quitToTitle(),
    });
    this.hud.sync(this.state);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  update(_t: number, dtMs: number): void {
    if (this.disposed) return;
    const pressed = sharedInput.consumePressed();
    if (pressed.cancel) {
      this.quitToTitle();
      return;
    }
    const dt = Math.min(0.05, dtMs / 1000);
    try {
      this.stage.sync(this.state, dt);
      this.hud.sync(this.state);
    } catch (err) {
      // Keep Quit/HUD responsive if a panel sync faults (e.g. mid-VFX teardown).
      console.error("[art-lab] sync failed", err);
    }
  }

  private quitToTitle(): void {
    if (this.disposed) return;
    // Match FFA/Dropfront: tear down HUD/stage before scene change so a
    // destroy error cannot leave the shell empty while still on Art Lab.
    if (shouldRestartOnQuit(SceneKey.ArtLab)) {
      assertTransition(SceneKey.ArtLab, SceneKey.ArtLab);
      this.teardown();
      this.scene.start(SceneKey.ArtLab);
      return;
    }
    assertTransition(SceneKey.ArtLab, SceneKey.Title);
    this.teardown();
    this.scene.start(SceneKey.Title);
  }

  private teardown(): void {
    if (this.disposed) return;
    this.disposed = true;
    try {
      this.stage?.destroy();
    } catch (err) {
      console.error("[art-lab] stage destroy failed", err);
    }
    try {
      this.hud?.destroy();
    } catch (err) {
      console.error("[art-lab] hud destroy failed", err);
    }
    clearOverlay();
  }
}
